import { useCallback } from 'react';
const defaultTimeout = 30000;

/**
 * Enhanced API Client with flexible content type and response type handling
 * 
 * Features:
 * - Configurable content types: application/json, multipart/form-data, text/plain, etc.
 * - Response types: json (default), text
 * - Automatic body processing for different data types (Object, FormData, String)
 * - Timeout handling with abort controller
 * - Mock data fallback for development
 * - Backwards compatibility with existing code
 */

// Simple fetch function with error handling and timeout
const fetchWithTimeout = async (url, timeout = defaultTimeout, endpoint = '', options = {}) => {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);

  try {
    // Extract content type from options or use default
    const contentType = options.contentType || options.headers?.['Content-Type'] || 'application/json';
    const responseType = options.responseType || 'json'; // 'json' (default), 'text'
    
    // Prepare headers
    const headers = {
      'Content-Type': contentType,
      ...options.headers,
    };

    // Remove our custom options from fetch options
    const { contentType: _, responseType: __, ...fetchOptions } = options;
    
    const finalFetchOptions = {
      signal: controller.signal,
      headers,
      ...fetchOptions,
    };

    const response = await fetch(url, finalFetchOptions);
    clearTimeout(timeoutId);

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    // Handle response based on responseType or content type
    return await parseResponse(response, endpoint, responseType, contentType);
  } catch (error) {
    clearTimeout(timeoutId);
    if (error.name === 'AbortError') {
      throw new Error('Request timeout');
    }
    throw error;
  }
};

// Helper function to parse response based on type
const parseResponse = async (response, endpoint, responseType, contentType) => {
  // Handle specific response types (only json and text supported)
  switch (responseType.toLowerCase()) {
     case 'blob':
      return await response.blob();
    case 'text':
      return await response.text();      
    case 'json':
    default:
      return await response.json();
  }
};

/**
 * Custom hook for API client operations
 * Provides a centralized way to make API calls with fallback to mock data
 * 
 * Usage examples:
 * - JSON: await get('/api/data') // Default JSON response
 * - Text: await get('/api/text', { responseType: 'text' }) // Text response
 * - Form data: await post('/api/upload', formData, { contentType: 'multipart/form-data' })
 * - Custom headers: await post('/api/data', body, { headers: { 'Authorization': 'Bearer token' } })
 */
export const useApiClient = () => {
  const baseURL = import.meta.env.DEV ? 'http://localhost:8080' : window.location.origin;

  // Generic GET request
  // Examples:
  // - await get('/api/data') // JSON response (default)
  // - await get('/api/text', { responseType: 'text' }) // Text response
  const get = useCallback(async (endpoint, options = {}) => {
    const fullUrl = `${baseURL}${endpoint}`;

    try {
      const data = await fetchWithTimeout(fullUrl, defaultTimeout, endpoint, {
        method: 'GET',
        ...options,
      });
      return data;
    } catch (error) {
      console.warn(`GET request failed for ${endpoint}, using mock data:`, error.message);
      throw error;
    }
  }, [baseURL]);

  // Generic POST request
  // Examples:
  // - await post('/api/data', jsonData) // JSON data (default)
  // - await post('/api/upload', formData, { contentType: 'multipart/form-data' }) // File upload
  // - await post('/api/text', textData, { contentType: 'text/plain', responseType: 'text' }) // Plain text
  const post = useCallback(async (endpoint, body, options = {}) => {
    const fullUrl = `${baseURL}${endpoint}`;

    try {
      // Handle different body types
      let processedBody = body;
      let contentType = options.contentType;
      
      // Auto-detect content type for FormData
      if (body instanceof FormData) {
        processedBody = body;
        contentType = undefined; // Let browser set boundary for multipart/form-data
      } else if (body && typeof body === 'object' && !contentType) {
        processedBody = JSON.stringify(body);
        contentType = 'application/json';
      } else if (typeof body === 'string' && !contentType) {
        processedBody = body;
        contentType = 'text/plain';
      }

      const data = await fetchWithTimeout(fullUrl, defaultTimeout, endpoint, {
        method: 'POST',
        body: processedBody,
        contentType,
        ...options,
      });
      return data;
    } catch (error) {
      console.warn(`POST request failed for ${endpoint}, using mock data:`, error.message);
      throw error;
    }
  }, [baseURL]);

  // Generic PUT request
  // Examples:
  // - await put('/api/user/123', userData) // JSON update (default)
  // - await put('/api/user/123/avatar', formData, { contentType: 'multipart/form-data' }) // File update
  const put = useCallback(async (endpoint, body, options = {}) => {
    const fullUrl = `${baseURL}${endpoint}`;

    try {
      // Handle different body types (same logic as POST)
      let processedBody = body;
      let contentType = options.contentType;
      
      if (body instanceof FormData) {
        processedBody = body;
        contentType = undefined; // Let browser set boundary
      } else if (body && typeof body === 'object' && !contentType) {
        processedBody = JSON.stringify(body);
        contentType = 'application/json';
      } else if (typeof body === 'string' && !contentType) {
        processedBody = body;
        contentType = 'text/plain';
      }

      const data = await fetchWithTimeout(fullUrl, defaultTimeout, endpoint, {
        method: 'PUT',
        body: processedBody,
        contentType,
        ...options,
      });
      return data;
    } catch (error) {
      console.warn(`PUT request failed for ${endpoint}, using mock data:`, error.message);
      throw error;
    }
  }, [baseURL]);

  // Generic DELETE request
  const del = useCallback(async (endpoint, options = {}) => {
    const fullUrl = `${baseURL}${endpoint}`;

    try {
      const data = await fetchWithTimeout(fullUrl, defaultTimeout, endpoint, {
        method: 'DELETE',
        ...options,
      });
      return data;
    } catch (error) {
      console.warn(`DELETE request failed for ${endpoint}, using mock data:`, error.message);
      throw error;
    }
  }, [baseURL]);

  // Generic request function for custom methods
  const request = useCallback(async (endpoint, options = {}) => {
    const fullUrl = `${baseURL}${endpoint}`;

    try {
      const data = await fetchWithTimeout(fullUrl, defaultTimeout, endpoint, options);
      return data;
    } catch (error) {
      console.warn(`Request failed for ${endpoint}, using mock data:`, error.message);
      throw error;
    }
  }, [baseURL]);

  return {
    // New generic methods
    get,
    post,
    put,
    delete: del,
    request,
    baseURL
  };
};
